/**
 * AdminRegionVenues — venue assignment surface (BON-153), from the design's
 * region-venue-assignment.jsx. Venues plotted on the map + a roster, each with
 * a per-venue exception (auto / pin / exclude). Route /admin/regions/venues.
 */
import { Suspense, lazy, useMemo, useState } from 'react';

import { KpiStrip, type KpiCell } from '../../primitives';
import { BucketError, BucketLoading, SectionCard } from '../../regional/BucketStates';

import { buildColorMap } from './RegionHierarchyTree';
import {
  useMapVenues,
  useRegionTree,
  useSetVenueException,
  useTriggerGeocoding,
} from './regionsQueries';
import { VenueExceptionControl } from './VenueExceptionControl';
import { VenueList } from './VenueList';

const RegionVenueMap = lazy(() =>
  import('./RegionVenueMap').then((m) => ({ default: m.RegionVenueMap })),
);

function venueKpis(counts: {
  auto: number;
  pin: number;
  exclude: number;
  unassigned: number;
}): KpiCell[] {
  return [
    { label: 'Auto-assigned', value: String(counts.auto) },
    { label: 'Pinned', value: String(counts.pin) },
    { label: 'Excluded', value: String(counts.exclude) },
    { label: 'Unassigned', value: String(counts.unassigned), good: counts.unassigned === 0 },
  ];
}

export default function AdminRegionVenues() {
  const venuesQ = useMapVenues();
  const treeQ = useRegionTree();
  const setException = useSetVenueException();
  const geocode = useTriggerGeocoding();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const venues = useMemo(() => venuesQ.data ?? [], [venuesQ.data]);
  const colorMap = useMemo(() => buildColorMap(treeQ.data ?? []), [treeQ.data]);

  if (venuesQ.isLoading) return <BucketLoading />;
  if (venuesQ.isError) return <BucketError error={venuesQ.error} />;

  const counts = {
    auto: venues.filter((v) => v.exception === 'auto' && v.regionId).length,
    pin: venues.filter((v) => v.exception === 'pin').length,
    exclude: venues.filter((v) => v.exception === 'exclude').length,
    unassigned: venues.filter((v) => !v.regionId && v.exception !== 'exclude').length,
  };
  const colorOf = (regionId: string | null) =>
    regionId ? (colorMap.get(regionId) ?? '#8a948c') : '#8a948c';
  const selected = venues.find((v) => v.id === selectedId) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <KpiStrip items={venueKpis(counts)} />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="nb-btn" disabled={geocode.isPending} onClick={() => geocode.mutate()}>
          {geocode.isPending ? 'Re-geocoding…' : 'Re-geocode venues'}
        </button>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
          gap: 14,
        }}
      >
        <SectionCard title="Venues">
          <div style={{ height: 420 }}>
            <Suspense fallback={<div style={{ height: '100%' }} />}>
              <RegionVenueMap venues={venues} colorMap={colorMap} selectedRegionId={null} />
            </Suspense>
          </div>
        </SectionCard>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
          <SectionCard title={`Venues · ${venues.length}`}>
            <div style={{ maxHeight: 240, overflow: 'auto', margin: '0 -6px' }}>
              <VenueList
                venues={venues}
                selectedId={selectedId}
                onSelect={setSelectedId}
                colorOf={colorOf}
              />
            </div>
          </SectionCard>

          <SectionCard title="Exception">
            {selected ? (
              <VenueExceptionControl
                venue={selected}
                regions={treeQ.data ?? []}
                pending={setException.isPending}
                onApply={(mode, regionId) =>
                  setException.mutate({ venueId: selected.id, mode, regionId })
                }
              />
            ) : (
              <div style={{ fontSize: 13, color: 'var(--nb-subtle)' }}>
                Select a venue to set a pin or exclusion.
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
